<template>
  <div class="form">
    <div class="row">
      <div class="col-md-12">
        <div class="availability-message">
          <small>
            {{ $t('BookingInfoAvailabilityMessage1')}}<br>
            {{ $t('BookingInfoAvailabilityMessage2')}}<br>
            <ul>
              <li>{{ $t('BookingInfoAvailabilityMessage3')}}</li>
              <li>{{ $t('BookingInfoAvailabilityMessage4')}}</li>
            </ul>
          </small>
        </div>
      </div>
    </div>
    <div class="row form-group">
      <label class="col-sm-3 col-form-label">
        {{ $t('Ride type') }}
      </label>
      <div class="col-sm-9">
        <div class="form-check-inline">
          <label class="form-check-label">
            <input type="radio"
                   class="form-check-input"
                   value="normal"
                   v-model="rideType"
            > {{ $t('Normal ride') }}
          </label>
        </div>
        <div class="form-check-inline" v-show="tripType!=='return'">
          <label class="form-check-label">
            <input type="radio"
                   class="form-check-input"
                   value="priority"
                   v-model="rideType"
            > {{ $t('Priority ride') }}
          </label>
        </div>
      </div>
    </div>
    <div class="row form-group" v-show="isPriority">
      <label class="col-sm-3 col-form-label">
        {{ $t('Priority type') }}
      </label>
      <div class="col-sm-9">
        <div class="form-check-inline" v-for="e in priorityRadios">
          <label class="form-check-label">
            <input type="radio"
                   class="form-check-input"
                   :value="e.value"
                   v-model="priorityType"
                   @click="priorityType= e.value===priorityType ? '' : priorityType"
            > {{ e.text }}
          </label>
        </div>
      </div>
    </div>

    <div class="row form-group">
      <label class="col-sm-3 col-form-label">
        {{ $t('Scheduled time') }}
      </label>
      <div class="col-sm-6">
        <div class="input-group pb-2">
          <div class="form-check-inline">
            <label class="form-check-label">
              <input type="radio"
                     class="form-check-input"
                     :checked="!isPriority"
                     :disabled="isPriority"
                     value="pickup"
              > {{ $t('Pickup') }}
            </label>
          </div>
          <div class="form-check-inline">
            <label class="form-check-label">
              <input type="radio"
                     class="form-check-input"
                     :checked="isPriority"
                     :disabled="!isPriority"
                     value="dropoff"
              > {{ $t('Dropoff') }}
            </label>
          </div>
        </div>
        <datetime-picker-separated
          v-model="scheduledDatetime"
          :errorMessage="scheduledDateError"
          @notComplete="scheduledDatetimePartialCompleteEvent"
        />
      </div>
    </div>
    <hr />
    <div class="row form-group">
      <label class="col-sm-3 col-form-label">
        {{ $t('From') }}
      </label>
      <div class="col-sm-9">
        <geo-complete :placeholder="$t('Pickup address')" v-model="pickup"></geo-complete>
        <div v-if="pickupError" class="text-danger d-block">
          <small>{{ pickupError }}</small>
        </div>
      </div>
    </div>
    <hr />
    <div class="row form-group">
      <label class="col-sm-3 col-form-label">
        {{ $t('To') }}
      </label>
      <div class="col-sm-9">
        <geo-complete :placeholder="$t('Dropoff address')" v-model="dropoff"></geo-complete>
        <div v-if="dropoffError" class="text-danger d-block">
          <small>{{ dropoffError }}</small>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-9 offset-sm-3">
        <div  v-if="routing.working || routeError" class="mb-4">
          <div v-if="routing.working">
            <b-spinner small variant="secondary"></b-spinner>
            <small class="text-muted"> {{ $t('Calculating route') }} </small>
          </div>
          <div v-if="routeError" class="text-danger d-block">
            <small>{{ routeError }}</small>
          </div>
        </div>
      </div>
    </div>
    <div class="row form-group d-none">
      <label class="col-sm-3 col-form-label">
        {{ $t('Route') }}
      </label>
      <div class="col-sm-9">
        <label class="col-form-label">
          <div v-if="routing.working" class="spinner-border text-primary text-center"></div>
          <div class="text-danger d-block" v-if="routeError">
            {{ routeError }}
            <b-button variant="link"
                      v-if="routeError.trim().length"
                      @click="calculateRoute">{{ $t('Retry') }}</b-button>
          </div>
          {{ routeDisplayKm }}
        </label>

      </div>
    </div>
  </div>
</template>

<script>
  /* ============
   * Trip form
   * ============
   *
   * Do some desc
   */

  import SlotMixin from '@/mixins/slot';
  import State from '../store/modules/account/state';
  import GeoComplete from '@/components/GeoComplete';
  import DatetimePicker from '@/components/DatetimePicker';
  import DatetimePickerSeparated from '@/components/DatetimePickerSeparated';
  import Address from '@/models/Address';

  const TAGS= {
    //CALLBACK: "1cd29210-3c59-11eb-bd81-7909ec72cb90",
    WEDDING: "27a039e0-3c59-11eb-bd81-7909ec72cb90",
    FUNERAL: "27cbb6b0-3c59-11eb-bd81-7909ec72cb90",
    EXAM: "2787cfe0-3c59-11eb-bd81-7909ec72cb90",
    CHURCH: "27afca40-3c59-11eb-bd81-7909ec72cb90",
  };

  const Route = (e) =>{
    return ({
      distanceInMeters: '',
      polylineGeometry: '',
      timeInSeconds: '',
      ...e
    })
  };

  export default {
    name: 'TripForm',

    components: {
      State,
      GeoComplete,
      DatetimePicker,
      DatetimePickerSeparated,
    },

    mixins: [
      SlotMixin,
    ],

    props: {
      tripType: {
        type: String,
      },
      tripUid: {
        type: String,
      },
      pickupAddress: {
        type: Object, // Address
      },
      dropoffAddress: {
        type: Object, // Address
      },
      phoneNumber: {

      },
      originalTrip: {
        type: Object
      },
      withReturn: {
        type: Boolean,
        default: false
      }
    },

    computed: {
      isPriority() {
        return this.rideType === 'priority';
      },
      pickupDatetime() {
        return !this.isPriority ? this.scheduledDatetime : null;
      },
      dropoffDatetime() {
        return this.isPriority ? this.scheduledDatetime : null;
      },
      priorityRadios() {
        return _.transform(TAGS, (radios, uid, key)=>{
          radios.push({value: uid, text: this.$t(key)});
        },[]);
      },
      coordinates() {
        if( this.pickupGpsSet && this.dropoffGpsSet ) {
          return {
            origin: _.clone(this.pickup.gps),
            destination: _.clone(this.dropoff.gps),
            forType: 'BOOKING_APP',
          }
        }
        return false;
      },
      pickupGpsSet() {
        //console.log(this.pickup)
        return  this.pickup.gps.lat && this.pickup.gps.lon;
      },
      routeError() {
        if( this.route.distanceInMeters && this.route.distanceInMeters > 28000 ) {
          return this.$t('errors.routeDistanceOverLimit');
        }
        if( this.routing.error ) {
          return this.routing.error;
        }
        if( !this.route.distanceInMeters ) {
          return " "; // presutni this.$t('errors.routeNotCalculated')
        }
        return null;
      },
      routeDisplayKm() {
        let kms= parseFloat(this.route.distanceInMeters) / 1000;
        kms= Math.round((kms + Number.EPSILON) * 100) / 100; // round at most 2
        if( isNaN(kms) ) {
          return "N/A";
        } else {
          kms= kms.toFixed(2);
          kms = String(kms).replace(".", ",") + " km";
          return kms;
        }
      },
      dropoffGpsSet() {
        return this.dropoff.gps.lat && this.dropoff.gps.lon;
      },
      normalRideNotXTimeInFrontMessage() {
        let time= this.$humanizeDuration(this.$root.hardcodedMap.MIN_SECONDS.pickup*1000, {language: this.$root.locale})
        let msg= this.$t('errors.normalRideNotXTimeInFront', {time})
        if(this.$root.hardcodedMap.centralPhoneNumber) {
          msg+= ' '+this.$t('errors.normalRideNotXTimeInFrontAddition', {phoneNumber : this.$root.hardcodedMap.centralPhoneNumber})
        }
        return msg
      },
      scheduledDateError() {
        if(this.scheduledDatetimePartialComplete) {
          return this.$t('errors.bothDateAndTimeNeeded');
        }
        const scheduled= this.scheduledDatetime ? moment(this.scheduledDatetime) : null;
        if(!scheduled) {
          if(this.isPriority) {
            return this.$t('errors.priorityRideNot24InFront');
          } else {
            return this.normalRideNotXTimeInFrontMessage
            //return this.$t('errors.normalRideNot24HoursInFront');
          }
          return " "; // presutni error ne prikazuje se u display-u ali tu je
        }
        // check working hours
        const day = scheduled.day();
        if( _.includes([1,2,3,4], day) && !scheduled.isBetween( ...this.timeRange(scheduled, [7,30], [23,0]) )
          || day === 5 && !scheduled.isBetween( ...this.timeRange(scheduled, [7,30], [0,0]) )
          || day === 6 && !scheduled.isBetween( ...this.timeRange(scheduled, [8,59], [0,0]) )
          || day === 0 && !scheduled.isBetween( ...this.timeRange(scheduled, [8,59], [23,0]) )
        ){
          return this.$t('errors.outsideWorkingHours');
        }

        if(this.isPriority && moment().add(24, 'hours').isSameOrAfter(scheduled) ) {
          return this.$t('errors.priorityRideNot24InFront');
        }
        if( moment().add(this.$root.hardcodedMap.MIN_SECONDS.pickup, 'seconds').isSameOrAfter(scheduled) ) {
          return this.normalRideNotXTimeInFrontMessage
        }

        if( this.withReturn ) {
          const date = this.originalTrip.tripData.pickupEventDateTime || this.originalTrip.tripData.dropoffEventDateTime
          if( date && scheduled.isBefore(moment(date))) {
            return this.$t('errors.returnRideCanNotBeScheduledBeforeOriginalRide');
          }
        }
        return null;
      },
      isReady() {
        return _.compact([
          this.scheduledDateError,
          this.routeError,
        ])
      }
    },

    data() {
      return {
        rideTypeGroup: '',
        rideType: 'normal',
        priorityType: '',
        scheduledDatetime: '',
        scheduledDatetimePartialComplete: false,
        pickup: new Address(),
        dropoff: new Address(),
        route: Route(),
        routing: {
          error: '',
          working: false,
          xhr: null,
          res: null
        },
        pickupError: '',
        dropoffError: ''
      }
    },

    methods: {
      calculateRoute() {
        const self = this;
        const {coordinates} = this;
        const {pickup, dropoff}= this;
        if( coordinates ) {
          if( self.routing.xhr ) {
            self.routing.xhr.abort()
          }
          self.routing.error = '';

          self.pickupError= !pickup.streetHouseNumber ? self.$t('House number is mandatory') : ''
          self.dropoffError= !dropoff.streetHouseNumber ? self.$t('House number is mandatory') : ''
          if(self.pickupError || self.dropoffError) {
            return false;
          }

          self.routing.working = true;
          self.routing.xhr = $.ajax({
            type: 'post',
            url: process.env.VUE_APP_API_LOCATION + '/geo/routing',
            contentType: 'application/json',
            dataType: 'json',
            data: JSON.stringify(coordinates),
            headers: {
              Authorization: `Bearer ${localStorage.getItem('id_token')}`
            }
          })
            .done((route)=>{
              self.$set(self, 'route', Route(route))
            })
            .fail((jqXHR)=>{
              self.routing.error = _.get(jqXHR, 'responseJSON.message')
            })
            .always(()=>{
              self.routing.working = false;
            })
        }
      },
      timeRange(date, start, end) {
        const startDate= moment(date).hours(start[0]).minutes(start[1]);
        const endDate= moment(date).hours(end[0]).minutes(end[1]);
        if (startDate.isAfter(endDate)){
          endDate.add(1, 'days');
        }
        return [
          startDate,
          endDate
        ]
      },
      getData() {
        return JSON.parse(JSON.stringify(({
          tripData: {
            pickupEvent: {
              eventUid: this.$short.uuid(),
              eventLocation: this.pickup,
              eventDateTime: this.pickupDatetime,
            },
            pickupEventDateTime: this.pickupDatetime,
            dropoffEvent: {
              eventUid: this.$short.uuid(),
              eventLocation: this.dropoff,
              eventDateTime: this.dropoffDatetime,
            },
            dropoffEventDateTime: this.dropoffDatetime,
            tags: _.compact([this.priorityType])
          },
          route: this.route,
          isArrivalWarranty: !!this.dropoffDatetime
        })))
      },
      scheduledDatetimePartialCompleteEvent() {
        //console.log("PARTIAL", arguments)
      },
      populateFromTrip(isReturn) {
        this.$log.debug(this.$options.name, JSON.parse(JSON.stringify(this.originalTrip)))
        const { tripData } = this.originalTrip;
        if( isReturn ) {
          this.$set(this, 'pickup', new Address(tripData.dropoffEvent.eventLocation))
          this.$set(this, 'dropoff', new Address(tripData.pickupEvent.eventLocation))
          this.scheduledDatetime = moment(tripData.dropoffEventDateTime || tripData.pickupEventDateTime).add(4,'hours').toISOString();
        } else {
          // it's edit
          this.$set(this, 'pickup', new Address(tripData.pickupEvent.eventLocation))
          this.$set(this, 'dropoff', new Address(tripData.dropoffEvent.eventLocation))
          this.scheduledDatetime= tripData.dropoffEventDateTime || tripData.pickupEventDateTime;
          if( tripData.dropoffEventDateTime ) {
            this.rideType = 'priority'
            const priorityTypeUids= _.values(TAGS);
            _.forEach(tripData.tagUids, tagUid =>{
              if(_.includes(priorityTypeUids, tagUid)) {
                this.priorityType = tagUid;
              }
            })
          }
        }
      }
    },

    mounted() {
      this.$log.debug(this.$options.name, this);
      if(!_.isEmpty(this.pickupAddress)) {
        this.$set(this, 'pickup', new Address(this.pickupAddress))
      }
      if(!_.isEmpty(this.dropoffAddress)) {
        this.$set(this, 'dropoff', new Address(this.dropoffAddress))
      }
    },

    watch: {
      isReady(errors) {
        //console.log("ERRORS", errors)
        this.$emit('input', errors.length === 0 )
      },
      coordinates(coords) {
        if(coords) {
          this.calculateRoute()
        } else {
          this.$set(this, 'route', Route())
        }
      },
      $data: {
        handler: function(val, oldVal) {
          this.$emit('update', this.getData())
        },
        deep: true
      },
      withReturn(withReturn) {
        if(withReturn) {
          this.populateFromTrip(true)
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  .address-components {
    .row {
      background: #f1f1f1;
    }
  }
  .availability-message {
    border-radius: 2px;
    padding: 5px 10px 1px 10px;
    color: #fff;
    background: #6c757d;
    margin-bottom: 5px;
  }
</style>
